import { FC, useMemo, useState } from "react";
import { Card, Progress, Spinner, useThemeMode } from "flowbite-react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import {
    HiTicket,
    HiInbox,
    HiCheckCircle,
    HiClock,
    HiArrowSmUp,
    HiArrowSmDown,
    HiSparkles,
    HiOutlineLightningBolt,
    HiExclamationCircle,
} from "react-icons/hi";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { agentRoles } from "../constants/agent";
import { useSelectedCompany } from "../context/SelectedCompanyContext";
import SelectCompanyPrompt from "../components/SelectCompanyPrompt";
import SuperAdminCompanyBar from "../components/SuperAdminCompanyBar";
import { useGetDashboardOverviewQuery } from "../api/dashboardApi/dashboard";
import { useGetChannelDistributionReportQuery } from "../api/reportsApi/reports";
import type {
    DashboardActivityItem,
    DashboardOverviewResponse,
    DashboardPeriod,
    DashboardTopAgent,
} from "../types/dashboard";

interface KpiCardProps {
    label: string;
    value: string;
    delta: string;
    trend: "up" | "down" | "flat";
    icon: IconType;
    iconBg: string;
    iconColor: string;
    sublabel: string;
    invertedTrend?: boolean;
    extraNote?: string;
}

const KpiCard: FC<KpiCardProps> = ({
    label,
    value,
    delta,
    trend,
    icon: Icon,
    iconBg,
    iconColor,
    sublabel,
    extraNote,
}) => {
    const positive = trend === "up";
    const negative = trend === "down";
    return (
        <Card className="overflow-hidden">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {label}
                    </p>
                    <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                        {value}
                    </p>
                    <div className="mt-2 flex items-center gap-1.5">
                        <span
                            className={`inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full ${
                                positive
                                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                    : negative
                                      ? "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                                      : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                            }`}
                        >
                            {positive ? (
                                <HiArrowSmUp className="w-3.5 h-3.5" />
                            ) : negative ? (
                                <HiArrowSmDown className="w-3.5 h-3.5" />
                            ) : null}
                            {delta}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {sublabel}
                        </span>
                    </div>
                    {extraNote && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            {extraNote}
                        </p>
                    )}
                </div>
                <div
                    className={`flex items-center justify-center w-12 h-12 rounded-xl ${iconBg}`}
                >
                    <Icon className={`w-6 h-6 ${iconColor}`} />
                </div>
            </div>
        </Card>
    );
};

function formatDelta(deltaPct: number): {
    label: string;
    trend: "up" | "down" | "flat";
} {
    if (!isFinite(deltaPct) || Math.abs(deltaPct) < 0.05) {
        return { label: "0%", trend: "flat" };
    }
    const sign = deltaPct > 0 ? "+" : "-";
    return {
        label: `${sign}${Math.abs(deltaPct).toFixed(1)}%`,
        trend: deltaPct > 0 ? "up" : "down",
    };
}

function formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return "0s";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    if (m === 0) return `${s}s`;
    if (s === 0) return `${m}m`;
    return `${m}m ${s}s`;
}

function formatDayLabel(iso: string, locale: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString(locale, { weekday: "short", day: "2-digit" });
}

function periodToDateRange(period: DashboardPeriod): {
    start_date: string;
    end_date: string;
} {
    const today = new Date();
    const end_date = today.toISOString().split("T")[0];
    const start = new Date(today);
    if (period === "7d") start.setDate(today.getDate() - 6);
    else if (period === "30d") start.setDate(today.getDate() - 29);
    return { start_date: start.toISOString().split("T")[0], end_date };
}

function formatTimeAgo(iso: string, t: (k: string, opts?: any) => string): string {
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return "";
    const diffMin = Math.max(0, Math.round((Date.now() - ts) / 60000));
    if (diffMin < 60) return t("dashboard:minutesAgo", { count: diffMin });
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return t("dashboard:hoursAgo", { count: diffH });
    const diffD = Math.round(diffH / 24);
    return t("dashboard:daysAgo", { count: diffD });
}

const AGENT_GRADIENTS = [
    "from-pink-500 to-rose-500",
    "from-blue-500 to-indigo-500",
    "from-emerald-500 to-teal-500",
    "from-amber-500 to-orange-500",
    "from-violet-500 to-purple-500",
    "from-cyan-500 to-sky-500",
    "from-fuchsia-500 to-pink-500",
];

function activityText(
    item: DashboardActivityItem,
    t: (k: string, opts?: any) => string
): string {
    const id = item.ticket_ref ?? `#${item.ticket_id}`;
    if (item.type === "created") {
        return t("dashboard:activity.ticketCreated", {
            id,
            client: item.client_name ?? "—",
        });
    }
    return t("dashboard:activity.ticketResolved", {
        id,
        agent: item.agent_name ?? "—",
    });
}

const AdminHomeDashboard: FC = () => {
    const { t, i18n } = useTranslation(["dashboard", "nav"]);
    const { mode } = useThemeMode();
    const isDark = mode === "dark";
    const currentUser = useCurrentUser();
    const [period, setPeriod] = useState<DashboardPeriod>("7d");
    const isSuperAdmin = currentUser?.agent_role === agentRoles.super_admin;
    const { selectedCompany } = useSelectedCompany();
    const needsCompany = isSuperAdmin && !selectedCompany;

    const displayName =
        currentUser?.display_name || currentUser?.agent_username || "Admin";

    const { data, isFetching, isError, refetch } = useGetDashboardOverviewQuery(
        {
            period,
            ...(isSuperAdmin && selectedCompany
                ? { company_id: selectedCompany.id }
                : {}),
        },
        { refetchOnMountOrArgChange: true, skip: needsCompany }
    );

    const overview: DashboardOverviewResponse | undefined = data;

    const ticketsOverTime = useMemo<ApexOptions>(
        () => ({
            chart: {
                type: "area",
                height: 320,
                toolbar: { show: false },
                fontFamily: "inherit",
                background: "transparent",
            },
            stroke: { curve: "smooth", width: 3 },
            dataLabels: { enabled: false },
            colors: ["#6366f1", "#10b981"],
            fill: {
                type: "gradient",
                gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.4,
                    opacityTo: 0.05,
                    stops: [0, 100],
                },
            },
            xaxis: {
                categories:
                    overview?.ticketsOverTime.map((p) =>
                        formatDayLabel(p.date, i18n.language)
                    ) ?? [],
                labels: {
                    style: {
                        colors: isDark ? "#9ca3af" : "#6b7280",
                        fontSize: "12px",
                    },
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
            },
            yaxis: {
                labels: {
                    style: {
                        colors: isDark ? "#9ca3af" : "#6b7280",
                        fontSize: "12px",
                    },
                },
            },
            grid: {
                borderColor: isDark ? "#374151" : "#e5e7eb",
                strokeDashArray: 4,
                yaxis: { lines: { show: true } },
                xaxis: { lines: { show: false } },
            },
            legend: {
                position: "top",
                horizontalAlign: "right",
                labels: { colors: isDark ? "#d1d5db" : "#374151" },
                markers: { width: 10, height: 10, radius: 12 },
            },
            tooltip: { theme: isDark ? "dark" : "light" },
            theme: { mode: isDark ? "dark" : "light" },
        }),
        [isDark, overview?.ticketsOverTime, i18n.language]
    );

    const ticketsOverTimeSeries = useMemo(
        () => [
            {
                name: t("dashboard:charts.opened"),
                data: overview?.ticketsOverTime.map((p) => p.opened) ?? [],
            },
            {
                name: t("dashboard:charts.resolved"),
                data: overview?.ticketsOverTime.map((p) => p.resolved) ?? [],
            },
        ],
        [overview?.ticketsOverTime, t]
    );

    const statusDonutSeries = useMemo(
        () => overview?.ticketsByStatus.map((s) => s.count) ?? [],
        [overview?.ticketsByStatus]
    );

    const statusDonut = useMemo<ApexOptions>(() => {
        const totalLabel = statusDonutSeries.reduce((a, b) => a + b, 0);
        return {
            chart: {
                type: "donut",
                fontFamily: "inherit",
                background: "transparent",
            },
            labels:
                overview?.ticketsByStatus.map((s) => s.status_name) ?? [],
            colors: ["#3b82f6", "#f59e0b", "#10b981", "#a855f7", "#6b7280", "#ef4444"],
            legend: {
                position: "bottom",
                labels: { colors: isDark ? "#d1d5db" : "#374151" },
                markers: { width: 10, height: 10, radius: 12 },
            },
            dataLabels: {
                style: { fontSize: "12px", fontWeight: 600 },
            },
            stroke: { show: false },
            plotOptions: {
                pie: {
                    donut: {
                        size: "65%",
                        labels: {
                            show: true,
                            total: {
                                show: true,
                                label: t("dashboard:kpi.totalTickets"),
                                color: isDark ? "#f3f4f6" : "#111827",
                                fontSize: "14px",
                                fontWeight: 600,
                                formatter: () => totalLabel.toLocaleString(),
                            },
                            value: {
                                show: true,
                                color: isDark ? "#f3f4f6" : "#111827",
                                fontSize: "22px",
                                fontWeight: 700,
                            },
                        },
                    },
                },
            },
            theme: { mode: isDark ? "dark" : "light" },
            tooltip: { theme: isDark ? "dark" : "light" },
        };
    }, [isDark, t, overview?.ticketsByStatus, statusDonutSeries]);

    const channelLabel = (label: string): string => {
        const key = `dashboard:channel.${label}`;
        const translated = t(key);
        return translated === key ? label : translated;
    };

    const channelBar = useMemo<ApexOptions>(
        () => ({
            chart: {
                type: "bar",
                toolbar: { show: false },
                fontFamily: "inherit",
                background: "transparent",
            },
            plotOptions: {
                bar: {
                    borderRadius: 8,
                    columnWidth: "55%",
                    distributed: true,
                    dataLabels: { position: "top" },
                },
            },
            dataLabels: {
                enabled: true,
                offsetY: -22,
                style: {
                    fontSize: "11px",
                    fontWeight: 600,
                    colors: [isDark ? "#d1d5db" : "#374151"],
                },
            },
            colors: ["#6366f1", "#0ea5e9", "#22c55e", "#3b82f6", "#f97316"],
            xaxis: {
                categories:
                    overview?.ticketsByChannel.map((c) => channelLabel(c.label)) ?? [],
                labels: {
                    style: {
                        colors: isDark ? "#9ca3af" : "#6b7280",
                        fontSize: "12px",
                    },
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
            },
            yaxis: {
                labels: {
                    style: {
                        colors: isDark ? "#9ca3af" : "#6b7280",
                        fontSize: "12px",
                    },
                },
            },
            grid: {
                borderColor: isDark ? "#374151" : "#e5e7eb",
                strokeDashArray: 4,
                yaxis: { lines: { show: true } },
                xaxis: { lines: { show: false } },
            },
            legend: { show: false },
            tooltip: { theme: isDark ? "dark" : "light" },
            theme: { mode: isDark ? "dark" : "light" },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isDark, overview?.ticketsByChannel, t]
    );

    const channelBarSeries = useMemo(
        () => [
            {
                name: t("dashboard:charts.tickets"),
                data: overview?.ticketsByChannel.map((c) => c.count) ?? [],
            },
        ],
        [overview?.ticketsByChannel, t]
    );

    const channelDateRange = useMemo(() => periodToDateRange(period), [period]);

    const { data: channelReport, isFetching: isChannelFetching } =
        useGetChannelDistributionReportQuery(channelDateRange, {
            refetchOnMountOrArgChange: true,
        });

    const channelDonutSeries = useMemo(
        () => [
            channelReport?.summary?.resolved_by_ai ?? 0,
            channelReport?.summary?.ended_with_ticket ?? 0,
            channelReport?.summary?.transferred_to_live_agent ?? 0,
        ],
        [channelReport]
    );

    const channelDonutHasData = channelDonutSeries.some((n) => n > 0);

    const channelSummaryCards = useMemo(
        () => [
            {
                label: t("dashboard:channelDist.totalWidgetSessions"),
                value: channelReport?.summary?.total_widget_sessions ?? 0,
                color: "text-blue-600 dark:text-blue-400",
            },
            {
                label: t("dashboard:channelDist.resolvedByAi"),
                value: channelReport?.summary?.resolved_by_ai ?? 0,
                color: "text-emerald-600 dark:text-emerald-400",
            },
            {
                label: t("dashboard:channelDist.ticketsCreated"),
                value: channelReport?.summary?.ended_with_ticket ?? 0,
                color: "text-purple-600 dark:text-purple-400",
            },
            {
                label: t("dashboard:channelDist.transferredToLive"),
                value: channelReport?.summary?.transferred_to_live_agent ?? 0,
                color: "text-orange-600 dark:text-orange-400",
            },
        ],
        [t, channelReport]
    );

    const channelBarOptions = useMemo<ApexOptions>(
        () => ({
            chart: {
                type: "bar",
                toolbar: { show: false },
                fontFamily: "inherit",
                background: "transparent",
            },
            plotOptions: {
                bar: {
                    borderRadius: 6,
                    columnWidth: "55%",
                    distributed: true,
                    dataLabels: { position: "top" },
                },
            },
            dataLabels: {
                enabled: true,
                offsetY: -22,
                style: {
                    fontSize: "11px",
                    fontWeight: 600,
                    colors: [isDark ? "#d1d5db" : "#374151"],
                },
            },
            colors: ["#3b82f6", "#10b981", "#a855f7", "#f97316", "#6366f1"],
            xaxis: {
                categories: [
                    t("dashboard:channelDist.totalWidgetSessions"),
                    t("dashboard:channelDist.resolvedByAi"),
                    t("dashboard:channelDist.ticketsCreated"),
                    t("dashboard:channelDist.transferredToLive"),
                ],
                labels: {
                    style: { colors: isDark ? "#9ca3af" : "#6b7280", fontSize: "11px" },
                    rotate: -25,
                    trim: true,
                    hideOverlappingLabels: false,
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
            },
            yaxis: {
                labels: {
                    style: { colors: isDark ? "#9ca3af" : "#6b7280", fontSize: "12px" },
                },
            },
            grid: {
                borderColor: isDark ? "#374151" : "#e5e7eb",
                strokeDashArray: 4,
                yaxis: { lines: { show: true } },
                xaxis: { lines: { show: false } },
            },
            legend: { show: false },
            tooltip: { theme: isDark ? "dark" : "light" },
            theme: { mode: isDark ? "dark" : "light" },
        }),
        [isDark, t]
    );

    const channelBarChartSeries = useMemo(
        () => [
            {
                name: t("dashboard:channelDist.sessions"),
                data: [
                    channelReport?.summary?.total_widget_sessions ?? 0,
                    channelReport?.summary?.resolved_by_ai ?? 0,
                    channelReport?.summary?.ended_with_ticket ?? 0,
                    channelReport?.summary?.transferred_to_live_agent ?? 0,
                ],
            },
        ],
        [t, channelReport]
    );

    const channelBarHasData = channelBarChartSeries[0].data.some((n) => n > 0);

    const channelDonutOptions = useMemo<ApexOptions>(() => {
        const total = channelDonutSeries.reduce((a, b) => a + b, 0);
        return {
            chart: {
                type: "donut",
                fontFamily: "inherit",
                background: "transparent",
            },
            labels: [
                t("dashboard:channelDist.resolvedByAi"),
                t("dashboard:channelDist.ticket"),
                t("dashboard:channelDist.liveAgent"),
            ],
            colors: ["#10b981", "#a855f7", "#f97316"],
            legend: {
                position: "bottom",
                labels: { colors: isDark ? "#d1d5db" : "#374151" },
                markers: { width: 10, height: 10, radius: 12 },
            },
            dataLabels: {
                style: { fontSize: "12px", fontWeight: 600 },
            },
            stroke: { show: false },
            plotOptions: {
                pie: {
                    donut: {
                        size: "65%",
                        labels: {
                            show: true,
                            total: {
                                show: true,
                                label: t("dashboard:channelDist.totalWidgetSessions"),
                                color: isDark ? "#f3f4f6" : "#111827",
                                fontSize: "13px",
                                fontWeight: 600,
                                formatter: () => total.toLocaleString(),
                            },
                            value: {
                                show: true,
                                color: isDark ? "#f3f4f6" : "#111827",
                                fontSize: "22px",
                                fontWeight: 700,
                            },
                        },
                    },
                },
            },
            theme: { mode: isDark ? "dark" : "light" },
            tooltip: { theme: isDark ? "dark" : "light" },
        };
    }, [isDark, t, channelDonutSeries]);

    const periods: Array<{ key: DashboardPeriod; label: string }> = [
        { key: "today", label: t("dashboard:today") },
        { key: "7d", label: t("dashboard:last7days") },
        { key: "30d", label: t("dashboard:last30days") },
    ];

    const totalTicketsDelta = formatDelta(
        overview?.kpis.totalTickets.deltaPct ?? 0
    );
    const openTicketsDelta = formatDelta(
        overview?.kpis.openTickets.deltaPct ?? 0
    );
    const resolvedInPeriodDelta = formatDelta(
        overview?.kpis.resolvedInPeriod.deltaPct ?? 0
    );
    const resolutionTimeDelta = formatDelta(
        overview?.kpis.avgResolutionTimeSeconds?.deltaPct ?? 0
    );

    // Super admin hasn't picked a company yet — prompt instead of showing stats.
    if (needsCompany) {
        return (
            <NavbarSidebarLayout>
                <div className="px-4 sm:px-6 pt-6">
                    <SuperAdminCompanyBar />
                    <SelectCompanyPrompt />
                </div>
            </NavbarSidebarLayout>
        );
    }

    return (
        <NavbarSidebarLayout>
            <div className="px-4 sm:px-6 pt-6 pb-12">
                <SuperAdminCompanyBar />
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
                    <div>
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 mb-3">
                            <HiSparkles className="w-3.5 h-3.5 text-purple-500" />
                            <span className="text-xs font-semibold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
                                {t("dashboard:title")}
                            </span>
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                            {t("dashboard:welcome", { name: displayName })}
                        </h1>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {t("dashboard:subtitle")}
                        </p>
                    </div>
                    <div className="inline-flex p-1 bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 self-start lg:self-auto">
                        {periods.map((p) => (
                            <button
                                key={p.key}
                                onClick={() => setPeriod(p.key)}
                                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all ${
                                    period === p.key
                                        ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                                        : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                                }`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                {isError && (
                    <Card className="mb-6 border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-rose-700 dark:text-rose-300">
                                <HiExclamationCircle className="w-5 h-5" />
                                <span className="font-medium">
                                    {t("dashboard:error.loadFailed")}
                                </span>
                            </div>
                            <button
                                onClick={() => refetch()}
                                className="text-sm font-semibold text-rose-700 dark:text-rose-300 hover:underline"
                            >
                                {t("dashboard:error.retry")}
                            </button>
                        </div>
                    </Card>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                    <KpiCard
                        label={t("dashboard:kpi.totalTickets")}
                        value={
                            overview?.kpis.totalTickets.current.toLocaleString() ?? "—"
                        }
                        delta={totalTicketsDelta.label}
                        trend={totalTicketsDelta.trend}
                        icon={HiTicket}
                        iconBg="bg-blue-50 dark:bg-blue-900/30"
                        iconColor="text-blue-600 dark:text-blue-400"
                        sublabel={t("dashboard:kpi.vsPrevious")}
                    />
                    <KpiCard
                        label={t("dashboard:kpi.openTickets")}
                        value={
                            overview?.kpis.openTickets.current.toLocaleString() ?? "—"
                        }
                        delta={openTicketsDelta.label}
                        trend={openTicketsDelta.trend}
                        icon={HiInbox}
                        iconBg="bg-amber-50 dark:bg-amber-900/30"
                        iconColor="text-amber-600 dark:text-amber-400"
                        sublabel={t("dashboard:kpi.vsPrevious")}
                    />
                    <KpiCard
                        label={t("dashboard:kpi.resolved")}
                        value={
                            overview?.kpis.resolvedInPeriod.current.toLocaleString() ?? "—"
                        }
                        delta={resolvedInPeriodDelta.label}
                        trend={resolvedInPeriodDelta.trend}
                        icon={HiCheckCircle}
                        iconBg="bg-emerald-50 dark:bg-emerald-900/30"
                        iconColor="text-emerald-600 dark:text-emerald-400"
                        sublabel={t("dashboard:kpi.vsPrevious")}
                        extraNote={t("dashboard:kpi.todayCount", {
                            count: overview?.kpis.resolvedToday.current ?? 0,
                        })}
                    />
                    <KpiCard
                        label={t("dashboard:kpi.avgResolutionTime")}
                        value={formatDuration(
                            overview?.kpis.avgResolutionTimeSeconds?.current ?? 0
                        )}
                        delta={resolutionTimeDelta.label}
                        trend={resolutionTimeDelta.trend}
                        icon={HiClock}
                        iconBg="bg-violet-50 dark:bg-violet-900/30"
                        iconColor="text-violet-600 dark:text-violet-400"
                        sublabel={t("dashboard:kpi.vsPrevious")}
                    />
                </div>

                {isFetching && !overview && (
                    <Card className="mb-6">
                        <div className="flex items-center justify-center py-16 gap-3 text-gray-500 dark:text-gray-400">
                            <Spinner size="md" />
                            <span>{t("dashboard:loading")}</span>
                        </div>
                    </Card>
                )}

                {overview && (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                            <div className="lg:col-span-2">
                                <Card className="h-full">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-base font-bold text-gray-900 dark:text-white">
                                            {t("dashboard:charts.ticketsOverTime")}
                                        </h3>
                                        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                                            <HiOutlineLightningBolt className="w-3.5 h-3.5 text-amber-500" />
                                            {periods.find((p) => p.key === period)?.label}
                                        </span>
                                    </div>
                                    <ReactApexChart
                                        options={ticketsOverTime}
                                        series={ticketsOverTimeSeries}
                                        type="area"
                                        height={320}
                                    />
                                </Card>
                            </div>
                            <Card>
                                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">
                                    {t("dashboard:charts.ticketsByStatus")}
                                </h3>
                                {statusDonutSeries.length > 0 ? (
                                    <ReactApexChart
                                        options={statusDonut}
                                        series={statusDonutSeries}
                                        type="donut"
                                        height={340}
                                    />
                                ) : (
                                    <EmptyState text={t("dashboard:empty.noData")} />
                                )}
                            </Card>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                            <Card>
                                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">
                                    {t("dashboard:charts.ticketsByChannel")}
                                </h3>
                                {overview.ticketsByChannel.length > 0 ? (
                                    <ReactApexChart
                                        options={channelBar}
                                        series={channelBarSeries}
                                        type="bar"
                                        height={300}
                                    />
                                ) : (
                                    <EmptyState text={t("dashboard:empty.noData")} />
                                )}
                            </Card>

                            <div className="lg:col-span-2">
                                <Card>
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-base font-bold text-gray-900 dark:text-white">
                                            {t("dashboard:charts.topAgents")}
                                        </h3>
                                    </div>
                                    {overview.topAgents.length > 0 ? (
                                        <TopAgentsTable
                                            agents={overview.topAgents}
                                            t={t}
                                        />
                                    ) : (
                                        <EmptyState
                                            text={t("dashboard:empty.noAgents")}
                                        />
                                    )}
                                </Card>
                            </div>
                        </div>

                        <div className="flex items-center justify-between mb-3 mt-2">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                {t("dashboard:channelDist.title")}
                            </h2>
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                                <HiOutlineLightningBolt className="w-3.5 h-3.5 text-amber-500" />
                                {periods.find((p) => p.key === period)?.label}
                            </span>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                            {channelSummaryCards.map((card, idx) => (
                                <Card key={idx}>
                                    <p className="text-xs font-medium tracking-tight text-gray-500 dark:text-gray-400">
                                        {card.label}
                                    </p>
                                    <p className={`mt-1 text-2xl font-bold ${card.color}`}>
                                        {isChannelFetching && !channelReport ? "—" : card.value.toLocaleString()}
                                    </p>
                                </Card>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                            <Card>
                                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">
                                    {t("dashboard:channelDist.sessionDistribution")}
                                </h3>
                                {isChannelFetching && !channelReport ? (
                                    <div className="flex items-center justify-center h-72 gap-2 text-gray-500 dark:text-gray-400">
                                        <Spinner size="sm" />
                                        <span className="text-sm">{t("dashboard:loading")}</span>
                                    </div>
                                ) : channelDonutHasData ? (
                                    <ReactApexChart
                                        options={channelDonutOptions}
                                        series={channelDonutSeries}
                                        type="donut"
                                        height={300}
                                    />
                                ) : (
                                    <EmptyState text={t("dashboard:empty.noData")} />
                                )}
                            </Card>

                            <Card>
                                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">
                                    {t("dashboard:channelDist.metricsOverview")}
                                </h3>
                                {isChannelFetching && !channelReport ? (
                                    <div className="flex items-center justify-center h-72 gap-2 text-gray-500 dark:text-gray-400">
                                        <Spinner size="sm" />
                                        <span className="text-sm">{t("dashboard:loading")}</span>
                                    </div>
                                ) : channelBarHasData ? (
                                    <ReactApexChart
                                        options={channelBarOptions}
                                        series={channelBarChartSeries}
                                        type="bar"
                                        height={300}
                                    />
                                ) : (
                                    <EmptyState text={t("dashboard:empty.noData")} />
                                )}
                            </Card>
                        </div>

                        <div className="grid grid-cols-1 gap-4 mb-6">
                            <Card>
                                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">
                                    {t("dashboard:channelDist.performanceRates")}
                                </h3>
                                {isChannelFetching && !channelReport ? (
                                    <div className="flex items-center justify-center h-32 gap-2 text-gray-500 dark:text-gray-400">
                                        <Spinner size="sm" />
                                        <span className="text-sm">{t("dashboard:loading")}</span>
                                    </div>
                                ) : channelReport ? (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div>
                                            <div className="mb-1 flex justify-between text-sm font-medium text-gray-700 dark:text-gray-200">
                                                <span>{t("dashboard:channelDist.aiResolutionRate")}</span>
                                                <span>{channelReport.percentages?.ai_resolution_rate ?? 0}%</span>
                                            </div>
                                            <Progress
                                                progress={channelReport.percentages?.ai_resolution_rate ?? 0}
                                                color="teal"
                                                size="lg"
                                            />
                                        </div>
                                        <div>
                                            <div className="mb-1 flex justify-between text-sm font-medium text-gray-700 dark:text-gray-200">
                                                <span>{t("dashboard:channelDist.ticketCreationRate")}</span>
                                                <span>{channelReport.percentages?.ticket_rate ?? 0}%</span>
                                            </div>
                                            <Progress
                                                progress={channelReport.percentages?.ticket_rate ?? 0}
                                                color="purple"
                                                size="lg"
                                            />
                                        </div>
                                        <div>
                                            <div className="mb-1 flex justify-between text-sm font-medium text-gray-700 dark:text-gray-200">
                                                <span>{t("dashboard:channelDist.liveTransferRate")}</span>
                                                <span>{channelReport.percentages?.live_agent_rate ?? 0}%</span>
                                            </div>
                                            <Progress
                                                progress={channelReport.percentages?.live_agent_rate ?? 0}
                                                color="blue"
                                                size="lg"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <EmptyState text={t("dashboard:empty.noData")} />
                                )}
                            </Card>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            <Card>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-base font-bold text-gray-900 dark:text-white">
                                        {t("dashboard:charts.recentActivity")}
                                    </h3>
                                </div>
                                {overview.recentActivity.length === 0 ? (
                                    <EmptyState
                                        text={t("dashboard:empty.noActivity")}
                                    />
                                ) : (
                                    <ul className="space-y-3">
                                        {overview.recentActivity.map((item, i) => (
                                            <li
                                                key={`${item.type}-${item.ticket_id}-${i}`}
                                                className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                            >
                                                <span
                                                    className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                                                        item.type === "resolved"
                                                            ? "bg-emerald-500"
                                                            : "bg-blue-500"
                                                    }`}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm text-gray-700 dark:text-gray-200">
                                                        {activityText(item, t)}
                                                    </p>
                                                    <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                                                        {formatTimeAgo(item.at, t)}
                                                    </p>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Card>
                        </div>
                    </>
                )}
            </div>
        </NavbarSidebarLayout>
    );
};

const EmptyState: FC<{ text: string }> = ({ text }) => (
    <div className="flex items-center justify-center py-12 text-sm text-gray-400 dark:text-gray-500">
        {text}
    </div>
);

const TopAgentsTable: FC<{
    agents: DashboardTopAgent[];
    t: (k: string, opts?: any) => string;
}> = ({ agents, t }) => (
    <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm">
            <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <th className="pb-3 px-2">{t("dashboard:agentTable.agent")}</th>
                    <th className="pb-3 px-2 text-right">
                        {t("dashboard:agentTable.tickets")}
                    </th>
                    <th className="pb-3 px-2 text-right">
                        {t("dashboard:agentTable.resolved")}
                    </th>
                    <th className="pb-3 px-2 text-right">
                        {t("dashboard:agentTable.avgTime")}
                    </th>
                    <th className="pb-3 px-2 w-32" />
                </tr>
            </thead>
            <tbody>
                {agents.map((agent, idx) => {
                    const gradient =
                        AGENT_GRADIENTS[idx % AGENT_GRADIENTS.length];
                    const name = agent.display_name || agent.agent_username;
                    const initial = (name || "?").charAt(0).toUpperCase();
                    return (
                        <tr
                            key={agent.agent_id}
                            className="border-t border-gray-100 dark:border-gray-800"
                        >
                            <td className="py-3 px-2">
                                <div className="flex items-center gap-3">
                                    <div
                                        className={`w-9 h-9 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-sm font-bold shadow-sm`}
                                    >
                                        {initial}
                                    </div>
                                    <span className="font-semibold text-gray-900 dark:text-white">
                                        {name}
                                    </span>
                                </div>
                            </td>
                            <td className="py-3 px-2 text-right text-gray-900 dark:text-white font-medium">
                                {agent.tickets}
                            </td>
                            <td className="py-3 px-2 text-right text-emerald-600 dark:text-emerald-400 font-medium">
                                {agent.resolved}
                            </td>
                            <td className="py-3 px-2 text-right text-gray-500 dark:text-gray-400">
                                {formatDuration(agent.avgResponseTimeSeconds)}
                            </td>
                            <td className="py-3 px-2">
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full bg-gradient-to-r ${gradient}`}
                                            style={{ width: `${agent.score}%` }}
                                        />
                                    </div>
                                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-10 text-right">
                                        {agent.score}%
                                    </span>
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

export default AdminHomeDashboard;
